if (!customElements.get('luxury-testimonials')) {
  customElements.define(
    'luxury-testimonials',
    class LuxuryTestimonials extends HTMLElement {
      constructor() {
        super();
        this.viewport = this.querySelector('[data-testimonials-viewport]');
        this.track = this.querySelector('[data-testimonials-track]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.position = 0;
        this.loopWidth = 0;
        this.lastTime = 0;
        this.pauseReasons = new Set();
        this.pointerStart = 0;
        this.positionStart = 0;
        this.pointerMoved = false;
        this.suppressClick = false;
        this.frame = null;
        this.resizeObserver = null;
      }

      connectedCallback() {
        if (!this.viewport || !this.track || this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.originalItems = Array.from(this.track.children);
        this.speed = Number(this.dataset.speed) || 28;
        this.autoScroll = this.dataset.autoScroll === 'true';
        this.pauseOnHover = this.dataset.pauseOnHover === 'true';

        this.onPointerEnter = (event) => {
          if (event.pointerType === 'mouse' && this.pauseOnHover) this.pause('hover');
        };
        this.onPointerLeave = (event) => {
          if (event.pointerType === 'mouse') this.resume('hover');
        };
        this.onFocusIn = () => this.pause('focus');
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.resume('focus');
        };
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onClick = (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        };
        this.onVisibilityChange = () => {
          document.hidden ? this.pause('hidden') : this.resume('hidden');
        };
        this.onReducedMotionChange = () => this.setup();
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        this.onBlockDeselect = () => this.resume('editor');

        this.viewport.addEventListener('pointerenter', this.onPointerEnter);
        this.viewport.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        this.viewport.addEventListener('click', this.onClick, true);
        this.viewport.addEventListener('dragstart', this.preventNativeDrag);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        this.reducedMotion.addEventListener('change', this.onReducedMotionChange);

        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.viewport);
        this.setup();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.resizeObserver?.disconnect();
        this.viewport?.removeEventListener('pointerenter', this.onPointerEnter);
        this.viewport?.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport?.removeEventListener('pointermove', this.onPointerMove);
        this.viewport?.removeEventListener('pointerup', this.onPointerUp);
        this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
        this.viewport?.removeEventListener('click', this.onClick, true);
        this.viewport?.removeEventListener('dragstart', this.preventNativeDrag);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      }

      setup() {
        cancelAnimationFrame(this.frame);
        this.removeClones();
        this.position = 0;
        this.applyPosition();

        if (!this.autoScroll || this.reducedMotion.matches || this.originalItems.length < 2) {
          this.dataset.paused = 'true';
          return;
        }

        this.originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.testimonialClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
            element.setAttribute('tabindex', '-1');
          });
          this.track.appendChild(clone);
        });

        this.measure();
        this.lastTime = performance.now();
        this.frame = requestAnimationFrame(this.animate.bind(this));
      }

      removeClones() {
        this.track.querySelectorAll('[data-testimonial-clone]').forEach((clone) => clone.remove());
      }

      measure() {
        if (!this.originalItems.length) return;
        const first = this.originalItems[0];
        const last = this.originalItems[this.originalItems.length - 1];
        this.loopWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.loopWidth += gap;
        this.normalizePosition();
        this.applyPosition();
      }

      animate(time) {
        const elapsed = Math.min(time - this.lastTime, 50);
        this.lastTime = time;

        if (!this.pauseReasons.size && this.loopWidth > 0) {
          this.position -= (this.speed * elapsed) / 1000;
          this.normalizePosition();
          this.applyPosition();
        }

        this.frame = requestAnimationFrame(this.animate.bind(this));
      }

      normalizePosition() {
        if (!this.loopWidth) return;
        while (this.position <= -this.loopWidth) this.position += this.loopWidth;
        while (this.position > 0) this.position -= this.loopWidth;
      }

      applyPosition() {
        this.track.style.transform = `translate3d(${this.position}px, 0, 0)`;
      }

      pause(reason) {
        this.pauseReasons.add(reason);
        this.dataset.paused = 'true';
      }

      resume(reason) {
        this.pauseReasons.delete(reason);
        this.dataset.paused = this.pauseReasons.size ? 'true' : 'false';
        this.lastTime = performance.now();
      }

      handlePointerDown(event) {
        if (this.reducedMotion.matches || event.button !== 0) return;
        this.pause('drag');
        this.pointerStart = event.clientX;
        this.positionStart = this.position;
        this.pointerMoved = false;
        this.viewport.classList.add('is-dragging');
      }

      preventNativeDrag(event) {
        event.preventDefault();
      }

      handlePointerMove(event) {
        if (!this.pauseReasons.has('drag')) return;
        if (!this.pointerMoved && Math.abs(event.clientX - this.pointerStart) > 5) {
          this.pointerMoved = true;
          this.viewport.setPointerCapture(event.pointerId);
        }
        if (!this.pointerMoved) return;
        this.position = this.positionStart + event.clientX - this.pointerStart;
        this.normalizePosition();
        this.applyPosition();
      }

      handlePointerUp(event) {
        if (!this.pauseReasons.has('drag')) return;
        this.viewport.classList.remove('is-dragging');
        if (this.viewport.hasPointerCapture(event.pointerId)) {
          this.viewport.releasePointerCapture(event.pointerId);
        }
        if (this.pointerMoved) {
          this.suppressClick = true;
          window.setTimeout(() => {
            this.suppressClick = false;
          }, 0);
        }
        this.resume('drag');
      }

      handleBlockSelect(event) {
        if (!this.contains(event.target)) return;
        this.pause('editor');
        const block = event.target.closest('[data-testimonial-block]');
        if (!block || !this.loopWidth) return;
        this.position = -block.offsetLeft + Math.max((this.viewport.clientWidth - block.offsetWidth) / 2, 0);
        this.normalizePosition();
        this.applyPosition();
      }
    }
  );
}
